import { useCallback, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router";
import { useDebounce } from "use-debounce";

export interface ProjectsSearch {
  searchTerm: string;
  debouncedSearchTerm: string;
  searchPending: boolean;
  showSearch: boolean;
  highlight: string | null;
  setSearchTerm(searchTerm: string): void;
  clearSearch(): void;
  queryParams: URLSearchParams;
  portfolioIds: string[];
  setPortfolioIds: (selected: string[]) => void;
  isOwned: boolean | null;
  setIsOwned: (value: boolean | null) => void;
}

/** Extracts search-related state from the URL / routing state */
export const useProjectsSearch = (): ProjectsSearch => {
  const history = useHistory();
  const location = useLocation();
  const [portfolioIds, setPortfolioIds] = useState<string[]>([]);
  const [isOwned, setIsOwned] = useState<boolean | null>(null);
  const queryParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );
  const searchTerm = queryParams.get("q") ?? "";
  const [debouncedSearchTerm] = useDebounce(searchTerm, 300, { leading: true });
  const searchPending = searchTerm !== debouncedSearchTerm;
  const showSearch = debouncedSearchTerm.length >= 3;
  const highlight = showSearch ? debouncedSearchTerm : null;

  /** Updates the search term state via the "q" query parameter */
  const setSearchTerm = useCallback(
    (searchTerm: string) => {
      const queryParams = new URLSearchParams(history.location.search);
      searchTerm ? queryParams.set("q", searchTerm) : queryParams.delete("q");
      history.push({ search: `?${queryParams.toString()}` });
    },
    [history]
  );

  /** Resets the search state */
  const clearSearch = useCallback(() => {
    setSearchTerm("");
  }, [setSearchTerm]);

  return {
    searchTerm,
    debouncedSearchTerm,
    searchPending,
    showSearch,
    highlight,
    setSearchTerm,
    clearSearch,
    queryParams,
    portfolioIds,
    setPortfolioIds,
    isOwned,
    setIsOwned,
  };
};
